import { normalizeUserCode, useDeviceRequest, useSession, useSettleDevice } from '@church/data'
import {
  faArrowRight,
  faBan,
  faCheck,
  faCircleCheck,
  faCircleXmark,
  faPresentationScreenDuotone,
} from '@church/ui-icons'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { useState } from 'react'
import { z } from 'zod'
import { Button, ErrorNote, Field, Icon, Input, Spinner } from '../components/ui.tsx'

/**
 * Approving a desktop app's sign-in (/device?user_code=…, Phase 12e): ForShow
 * opened this page with the code it shows. Signed out, the guard sends people
 * to sign in and back here.
 */
export const Route = createFileRoute('/_authed/device')({
  validateSearch: z.object({ user_code: z.string().optional() }),
  component: DeviceScreen,
})

function DeviceScreen() {
  const search = Route.useSearch()
  const navigate = useNavigate()
  const { me } = useSession()
  const userCode = normalizeUserCode(search.user_code ?? '')
  const [typed, setTyped] = useState('')
  const request = useDeviceRequest(userCode)
  const settle = useSettleDevice()

  return (
    <div className="flex min-h-full flex-col items-center justify-center bg-surface-sunken px-4 py-10">
      <div className="mb-8 text-center">
        <span className="text-2xl font-bold text-content-default">ForHim</span>
      </div>
      <div className="w-full max-w-md space-y-4 rounded-xl border border-border-subtle bg-surface-raised p-6 shadow-sm sm:p-8">
        <h1 className="flex items-center gap-2 text-xl font-semibold text-content-default">
          <Icon icon={faPresentationScreenDuotone} className="size-6 text-content-brand" />
          Sign in an app
        </h1>

        {!userCode || request.isError ? (
          <form
            className="space-y-4"
            onSubmit={(e) => {
              e.preventDefault()
              void navigate({ to: '/device', search: { user_code: normalizeUserCode(typed) } })
            }}
          >
            <ErrorNote error={request.error} />
            <Field label="Code shown in the app">
              <Input
                value={typed}
                onChange={(e) => setTyped(e.target.value)}
                autoComplete="off"
                autoCapitalize="characters"
                required
              />
            </Field>
            <Button type="submit" icon={faArrowRight} className="w-full">
              Continue
            </Button>
          </form>
        ) : request.isPending ? (
          <Spinner />
        ) : settle.isSuccess ? (
          <p className="flex items-center gap-2 text-sm text-content-muted">
            <Icon
              icon={settle.data ? faCircleCheck : faCircleXmark}
              className={`size-5 ${settle.data ? 'text-success-solid' : 'text-danger-solid'}`}
            />
            {settle.data
              ? 'Approved. You can close this tab and return to the app.'
              : 'Denied. The app was not signed in.'}
          </p>
        ) : request.data.kind === 'settled' ? (
          <p className="text-sm text-content-muted">
            {request.data.approved
              ? 'Already approved. You can return to the app.'
              : `This request was ${request.data.status}.`}
          </p>
        ) : (
          <div className="space-y-4">
            <p className="text-sm text-content-default">
              <span className="font-semibold">{request.data.app}</span> wants to sign in as{' '}
              <span className="font-semibold">{me?.user.email}</span>.
            </p>
            <p className="text-sm text-content-muted">
              Check that the app shows the code{' '}
              <span className="font-mono font-semibold tracking-widest">{userCode}</span>. Approve
              only if you opened it yourself.
            </p>
            <ErrorNote error={settle.error} />
            <div className="flex gap-2">
              <Button
                variant="success"
                icon={faCheck}
                className="flex-1"
                disabled={settle.isPending}
                onClick={() => settle.mutate({ userCode, approve: true })}
              >
                Approve
              </Button>
              <Button
                variant="danger"
                icon={faBan}
                className="flex-1"
                disabled={settle.isPending}
                onClick={() => settle.mutate({ userCode, approve: false })}
              >
                Deny
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
